import { useCallback, useEffect, useMemo, useState } from 'react'
import { AnimatePresence, motion, type Transition, type Variants } from 'framer-motion'
import { useAtomValue, useSetAtom } from 'jotai'
import { useVirtualizer } from '@tanstack/react-virtual'
import { useSidepanelFindInPageOpen } from '@renderer/overlay/Sidepanel/atoms'
import { useFindInPageRangeExtractor } from '@renderer/overlay/Sidepanel/hooks/use-find-in-page-mount-all'
import { useOpenActivity } from '../../components/history/use-open-activity'
import { View } from '../../components/view'
import { ViewGroupLabel } from '../../components/view-group-label'
import { HistorySelectableRow } from './nested/selectable-row'
import { HistoryListSkeleton } from '../../components/history/history-list-skeleton'
import { getActivityTitle } from '@renderer/overlay/Sidepanel/utils/activity-helpers'
import { historyHasSelectionAtom, clearHistorySelectionAtom } from '@renderer/overlay/Sidepanel/stores/history-select'
import {
  historyFilterToType,
  useHistoryActivity,
  type HistoryFilter,
} from '@renderer/overlay/Sidepanel/hooks/use-history-activity'
import type { RecentActivity } from '@renderer/overlay/Sidepanel/types'
import { toTimeframeRows, timeframeRowKey } from '@renderer/overlay/Sidepanel/utils/group-by-timeframe'
import { getHistorySearchCopy } from './history-search-copy'
import { HistoryViewHeader } from './nested/header'
import { HistorySelectProvider } from './nested/history-select-context'
import { HistoryActionsBar } from './nested/history-actions-bar'
import { historyFilterAtom } from './nested/filter-menu'

const actionsBarTransition: Transition = {
  type: 'spring',
  bounce: 0,
  duration: 0.3,
}

const actionsBarVariants: Variants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0 },
}

// Start loading the next page when the user scrolls within this many rows of the end.
const FETCH_NEXT_PAGE_THRESHOLD = 10

/**
 * History View (HIG-5836): the native full-panel Recents/History surface —
 * every past chat, meeting, and action in one virtualized list, grouped into
 * calendar timeframes, with the type filter and search in the view header.
 */
export function HistoryView() {
  const handleItemClick = useOpenActivity('History')
  const filter = useAtomValue(historyFilterAtom)
  const [searchQuery, setSearchQuery] = useState('')
  const hasSelection = useAtomValue(historyHasSelectionAtom)
  const clearSelection = useSetAtom(clearHistorySelectionAtom)
  const {
    items: activity,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useHistoryActivity({ type: historyFilterToType(filter) })

  // The virtualizer measures against the body's scroll element, and the filter
  // reset scrolls it — View.Body owns it, so take it as a value.
  const [scrollElement, setScrollElement] = useState<HTMLDivElement | null>(null)
  const captureScrollElement = useCallback((element: HTMLDivElement | null) => setScrollElement(element), [])

  useEffect(() => {
    return () => {
      clearSelection()
    }
  }, [clearSelection])

  const filteredActivity = useMemo<Array<RecentActivity>>(() => {
    if (!searchQuery.trim()) {
      return activity
    }
    const query = searchQuery.toLowerCase()
    return activity.filter((item) => getActivityTitle(item).toLowerCase().includes(query))
  }, [activity, searchQuery])

  const rows = useMemo(() => toTimeframeRows(filteredActivity, (item) => item.unifiedUpdatedAt), [filteredActivity])
  const findInPageOpen = useSidepanelFindInPageOpen()
  const rangeExtractor = useFindInPageRangeExtractor()

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollElement,
    estimateSize: (index) => (rows[index]?.kind === 'label' ? (index === 0 ? 20 : 32) : 41),
    overscan: 6,
    rangeExtractor,
    getItemKey: (index) => {
      const row = rows[index]
      return row ? timeframeRowKey(row, (item) => `${item.type}:${item.id}`) : index
    },
  })

  const virtualItems = virtualizer.getVirtualItems()
  const totalSize = virtualizer.getTotalSize()

  // Search only matches loaded titles, so pause pagination while searching to
  // avoid fetching every page just to fill a short filtered result set. Find
  // pauses it too: mount-all renders the last row, which would otherwise read
  // as "scrolled to the end" and page in the user's entire history.
  const isSearching = searchQuery.trim().length > 0
  const lastVirtualIndex = virtualItems[virtualItems.length - 1]?.index ?? -1
  const canPaginate = !isSearching && !findInPageOpen && hasNextPage && !isFetchingNextPage
  useEffect(() => {
    if (!canPaginate || lastVirtualIndex < 0) {
      return
    }
    if (lastVirtualIndex >= rows.length - FETCH_NEXT_PAGE_THRESHOLD) {
      void fetchNextPage()
    }
  }, [canPaginate, lastVirtualIndex, rows.length, fetchNextPage])

  // Search matches loaded titles only and pauses pagination, so the copy is
  // scoped to what it actually looked at — see history-search-copy.ts.
  const { placeholder: searchPlaceholder, truncationHint } = useMemo(
    () => getHistorySearchCopy(filter, { hasUnloadedHistory: hasNextPage }),
    [filter, hasNextPage],
  )

  // Selection is global (not scoped to the visible type), so a filter switch
  // would otherwise leave the actions bar counting rows the user can no longer
  // see — and a bulk delete acting on them.
  const handleFilterChange = useCallback(
    (_filter: HistoryFilter) => {
      clearSelection()
      scrollElement?.scrollTo({ top: 0 })
    },
    [clearSelection, scrollElement],
  )

  return (
    <View>
      <HistoryViewHeader
        searchQuery={searchQuery}
        searchPlaceholder={searchPlaceholder}
        onSearchChange={setSearchQuery}
        onFilterChange={handleFilterChange}
      />

      <View.Body bottomInset="list" onScrollElement={captureScrollElement}>
        {isLoading ? (
          <HistoryListSkeleton />
        ) : rows.length === 0 ? (
          <View.Empty searchQuery={searchQuery} searchHint={truncationHint}>
            No history
          </View.Empty>
        ) : (
          <HistorySelectProvider items={filteredActivity}>
            <div className="relative w-full" style={{ height: totalSize }}>
              {virtualItems.map((virtualRow) => {
                const row = rows[virtualRow.index]
                if (!row) {
                  return null
                }
                return (
                  <div
                    key={virtualRow.key}
                    data-index={virtualRow.index}
                    ref={virtualizer.measureElement}
                    className="absolute left-0 top-0 w-full"
                    style={{ transform: `translateY(${virtualRow.start}px)` }}
                  >
                    {row.kind === 'label' ? (
                      <ViewGroupLabel isFirst={row.isFirst}>{row.label}</ViewGroupLabel>
                    ) : (
                      <div className="pb-[2px]">
                        <HistorySelectableRow item={row.item} onOpen={handleItemClick} selectIndex={row.itemIndex} />
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
            {isFetchingNextPage && (
              <div className="pt-[2px]">
                <HistoryListSkeleton count={3} />
              </div>
            )}
          </HistorySelectProvider>
        )}
      </View.Body>

      <AnimatePresence>
        {hasSelection && (
          <motion.div
            variants={actionsBarVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            transition={actionsBarTransition}
            className="absolute inset-x-0 z-10 flex justify-center"
            style={{ bottom: 16 }}
          >
            <div className="bg-background corner-squircle flex items-center rounded-full px-2 py-1.5 shadow-lg">
              <HistoryActionsBar />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </View>
  )
}
